// src/components/staff/StaffPortal.jsx
import { useState } from 'react';
import { useWmStaff } from '../../context/WmStaffContext';
import { useLocations, getDisplayName } from '../../context/LocationContext';

export default function StaffPortal({ onNavigate }) {
  const { wmCan, enterOrgView } = useWmStaff();
  const { locations } = useLocations();
  const [search, setSearch] = useState('');

  // Defense-in-depth guard
  if (!wmCan('internal.shop_profiles')) {
    return <div className="p-6 text-center text-[var(--wm-text-body)] text-[#7D93AE]">Access restricted.</div>;
  }

  const filtered = locations.filter(loc => {
    const name = getDisplayName(loc).toLowerCase();
    return name.includes(search.toLowerCase()) || loc.id.toLowerCase().includes(search.toLowerCase());
  });

  const handleEnter = (loc) => {
    enterOrgView(loc.id, getDisplayName(loc));
    onNavigate('dashboard');
  };

  return (
    <div className="flex-1 overflow-auto p-6 max-w-2xl mx-auto w-full">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-1">
          <div className="w-2 h-2 rounded-full bg-amber-400" style={{ boxShadow: '0 0 6px rgba(245,158,11,0.5)' }} />
          <span className="text-[var(--wm-text-caption)] font-bold uppercase tracking-widest text-amber-400">WrapMind Staff</span>
        </div>
        <h1 className="text-[var(--wm-text-heading)] font-bold text-[#F8FAFE]">Shop Portal</h1>
        <p className="text-[var(--wm-text-body)] text-[#7D93AE] mt-1">View the app as a specific shop location.</p>
      </div>

      {/* Search */}
      {locations.length > 4 && (
        <input
          type="search"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search locations..."
          className="w-full h-10 px-4 text-[var(--wm-text-body)] rounded-xl border border-[#243348] bg-[#0F1923] text-[#F8FAFE] placeholder-[#4A6380] focus:outline-none focus:ring-2 focus:ring-amber-400/40 mb-4"
        />
      )}

      {/* List */}
      <div className="space-y-2">
        {filtered.length === 0 && (
          <p className="text-[var(--wm-text-body)] text-[#7D93AE] text-center py-8">No locations found.</p>
        )}
        {filtered.map(loc => (
          <div
            key={loc.id}
            className="flex items-center justify-between px-4 py-3 rounded-xl border border-[#243348] bg-[#1B2A3E] hover:border-amber-400/30 transition-colors"
          >
            <div className="flex items-center gap-3 min-w-0">
              {loc.color && (
                <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: loc.color }} />
              )}
              <div className="min-w-0">
                <p className="text-[var(--wm-text-body)] font-semibold text-[#F8FAFE] truncate">{getDisplayName(loc)}</p>
                {loc.address && (
                  <p className="text-[var(--wm-text-meta)] text-[#7D93AE] mt-0.5 truncate">{loc.address}{loc.city ? `, ${loc.city}` : ''}</p>
                )}
              </div>
            </div>
            <button
              onClick={() => handleEnter(loc)}
              className="ml-4 px-4 py-1.5 text-[var(--wm-text-ui)] font-semibold rounded-lg bg-amber-400/10 text-amber-300 border border-amber-400/25 hover:bg-amber-400/20 transition-colors flex-shrink-0"
            >
              Enter
            </button>
          </div>
        ))}
      </div>

      <p className="text-[var(--wm-text-caption)] text-[#4A6380] text-center mt-6">
        All access is logged · {wmCan('org.write') ? 'Read + Write' : 'Read-only'}
      </p>
    </div>
  );
}
